import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	inject,
	input,
	Output,
	Signal
} from '@angular/core';

import { CypressTagDirective } from '@vlaio/cypress/core';
import { ApplicationService } from '@vlaio/mandates/self-registration/application-data';
import { UserEIDLoginDirective } from '@vlaio/shared/authentication';
import { SelfRegistrationRoutePaths } from '@vlaio/shared/route-paths';
import { CallToActionEntity, CallToActionEnum, ProductEntity } from '@vlaio/shared/types';
import {
	VlaioEventButtonComponent,
	VlaioExternalButtonComponent,
	VlaioInternalButtonComponent
} from '@vlaio/shared/ui/common';

import { ProductAnnotationComponent } from '../product-annotation/product-annotation.component';
@Component({
	selector: 'vlaio-product-cta',
	templateUrl: './product-cta.component.html',
	styleUrls: ['./product-cta.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		ProductAnnotationComponent,
		VlaioEventButtonComponent,
		VlaioInternalButtonComponent,
		VlaioExternalButtonComponent,
		UserEIDLoginDirective,
		CypressTagDirective
	]
})
export class ProductCTAComponent {
	/**
	 * Inject the applicationService. It will be false when the user is not authenticated.
	 */
	public readonly applicationService = inject(ApplicationService, { optional: true });
	/**
	 * The possible button types.
	 */
	public readonly ButtonActionTypes: typeof CallToActionEnum = CallToActionEnum;
	/**
	 * The route paths for the self registration section.
	 */
	public readonly routePaths: typeof SelfRegistrationRoutePaths = SelfRegistrationRoutePaths;

	/**
	 * The configuration that defines the button.
	 */
	public readonly callToAction: Signal<CallToActionEntity> = input.required();
	/**
	 * The id of the product, used for the `login` template.
	 */
	public readonly product: Signal<ProductEntity> = input.required<ProductEntity>();

	/**
	 * The route path for the button should the {@link callToAction} be of type `RequestAccessEloket`.
	 */
	public readonly routePath: Signal<{ type: 'internal' | 'external'; path: string | string[] }> = computed(() => {
		return this.applicationService?.isExecutor()
			? {
					type: 'internal',
					path: [SelfRegistrationRoutePaths.Application]
				}
			: {
					type: 'external',
					path: this.callToAction().link
				};
	});

	/**
	 * Emits when the action button is pressed.
	 */
	@Output() public trackCtaClicked: EventEmitter<void> = new EventEmitter<void>();

	/**
	 * When the type of the action entails that the product should be registered
	 * via the ELO selfRegistration section, we set it in the store.
	 */
	public handleEloRequestClicked(): void {
		this.applicationService?.setSelectedProduct(this.product());

		this.trackCtaClicked.emit();
	}
}
